import React, { useContext, useEffect, useState, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { Route, getParams, selectRoute } from "src/config/routes";
import { Error } from "src/screens/error/Error";
import { LOADING_TYPE } from "src/stores/krediflow/InvoiceEnums";
import { InvoiceForm } from "./form/InvoiceForm";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { SplitView } from "./splitView/SplitView";
import { InvoiceHistory } from "./history/InvoiceHistory";
import { InvoiceAccountings } from "./accountings/InvoiceAccountings";
import { useLocation, useHistory } from "react-router-dom";
import { ColorStyle } from "src/utils/Colors";
import styles from "./InvoiceDetails.module.css";
import { InvoiceWorkflowTab } from "./tabs/InvoiceWorkflowTab";
import { InvoiceActionTab } from "./tabs/InvoiceActionTab";
import { InvoiceDuplicatesModal } from "./duplicatesModal/InvoiceDuplicatesModal";
import { AiExtractionInProgressModal } from "./aiExtractionModal/AiExtractionInProgressModal";
import { InvoiceBatchProcessTab } from "./tabs/InvoiceBatchProcessTab";
import { InvoiceFilePreview } from "./preview/InvoiceFilePreview";
import { InvoicePinnedComments } from "./pinnedComments/InvoicePinnedComments";

// Define an enum for the tab names
enum InvoiceTab {
    Invoice = "invoice",
    History = "history"
}

const InvoiceDetailsBase = () => {
    const { t } = useTranslation();
    const {
        navStore,
        authStore,
        invoiceStore,
        uiStore,
        invoiceFormStore,
        invoiceFilePreviewStore,
        restartCancelInvoiceStore
    } = useContext(RootStoreContext);

    const location = useLocation();
    const history = useHistory();

    // Determine invoiceNumber from your route params
    const role = authStore.user?.role;
    let params = getParams(Route.invoiceDetails, role);
    const invoiceNumber = params["invoicenumber"];

    // State for active tab
    const defaultTab = InvoiceTab.Invoice;
    const searchParams = new URLSearchParams(location.search);
    const urlTabParam = searchParams.get("tab") === InvoiceTab.History ? InvoiceTab.History : defaultTab;
    const [activeTab, setActiveTab] = useState<InvoiceTab>(urlTabParam);
    useEffect(() => {
        const currentUrlTab = searchParams.get("tab") === InvoiceTab.History ? InvoiceTab.History : defaultTab;
        if (currentUrlTab !== activeTab) {
            setActiveTab(currentUrlTab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const handleTabChange = useCallback(
        (clickedTab: InvoiceTab) => {
            setActiveTab(clickedTab);

            const newSearchParams = new URLSearchParams(location.search);
            newSearchParams.set("tab", clickedTab);
            history.push({
                pathname: location.pathname,
                search: newSearchParams.toString()
            });
        },
        [history, location.pathname, location.search]
    );

    const handleInvoiceTabClick = useCallback(() => {
        handleTabChange(InvoiceTab.Invoice);
    }, [handleTabChange]);

    const handleHistoryTabClick = useCallback(() => {
        handleTabChange(InvoiceTab.History);
    }, [handleTabChange]);

    useEffect(() => {
        navStore.setTitle("");
        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);

        navStore.setBackbutton(
            selectRoute(Route.creditors, authStore.user?.role),
            t("screens.kredi_flow.sidebar_title").toString()
        );
    }, [navStore, authStore, t]);

    // Initialize Data
    useEffect(() => {
        // Clear any stale restart/cancel/delete modal state left over from a previously opened invoice
        restartCancelInvoiceStore.reset();

        async function initializeInvoiceData() {
            if (invoiceStore.isinvoiceId(invoiceNumber)) {
                await invoiceStore.reRoutePathToInvoiceNumber(invoiceNumber);
            } else {
                await invoiceStore.init(invoiceNumber);
            }
        }
        initializeInvoiceData();

        // Abort any in-flight AI detection when leaving this route or switching to a
        // different invoice — prevents a late response from mutating an unrelated invoice.
        return () => {
            invoiceFilePreviewStore.abortAiDetection();
        };
    }, [invoiceStore, invoiceNumber, invoiceFilePreviewStore, restartCancelInvoiceStore]);

    // After a new invoice was saved, replace the /new-invoice URL with the real invoice
    // number through the router, so the address bar, the tab links and a browser reload
    // all point to the created invoice.
    const newlyCreatedInvoiceNumber = invoiceStore.newlyCreatedInvoiceNumber;
    useEffect(() => {
        if (newlyCreatedInvoiceNumber !== undefined) {
            invoiceStore.setNewlyCreatedInvoiceNumber(undefined);
            history.replace(
                selectRoute(Route.invoiceDetails, authStore.user?.role, {
                    invoicenumber: newlyCreatedInvoiceNumber.toString()
                })
            );
        }
    }, [newlyCreatedInvoiceNumber, invoiceStore, history, authStore.user?.role]);

    // Keep the history timeline live while the history tab is open. The subscription's
    // initial emission also reloads the history when the tab is opened.
    const currentInvoice = invoiceStore.currentInvoice;
    useEffect(() => {
        if (activeTab === InvoiceTab.History && currentInvoice && !currentInvoice.isNewInvoice) {
            currentInvoice.startInvoiceHistoryUpdateSubscription();

            return () => {
                currentInvoice.stopInvoiceHistoryUpdateSubscription();
            };
        }
    }, [activeTab, currentInvoice]);

    const toggleDisplayDuplicateModal = useCallback(() => {
        if (invoiceStore.currentInvoice) {
            invoiceStore.currentInvoice.setIsDuplicateModalDisplayed(
                !invoiceStore.currentInvoice!.isDuplicateModalDisplayed
            );
        }
    }, [invoiceStore.currentInvoice]);

    const onResetForm = useCallback(async () => {
        await invoiceFormStore.resetForm();
    }, [invoiceFormStore]);

    const saveFormButtonLabel = (
        <div className="d-flex align-items-center">
            <div>{t("labels.save")}</div>
            <UI.RotatingSpinner
                className={`${styles.FromSaveButtonSpinner} ${
                    invoiceStore.loadingType !== LOADING_TYPE.SAVING_INVOICE ? "d-none" : ""
                }`}
                noLogo={true}
                size={20}
            />
        </div>
    );

    if (invoiceStore.error && invoiceStore.error.loadingInvoice) {
        return <Error message={invoiceStore.error.loadingInvoice} />;
    }

    // The split view or mobile invoice form
    const desktopViewElements = (
        <>
            <SplitView />
            {invoiceStore.isEditing && (
                <div
                    style={{ zIndex: 8, position: "relative", display: "flex", marginTop: "1rem", marginLeft: "-15px" }}
                >
                    <UI.Button
                        id="invoice-save-button"
                        onClick={
                            invoiceStore.currentInvoice?.isNewInvoice && !invoiceStore.currentInvoice.isFileSelected
                                ? invoiceFormStore.toggleDisplaySaveInvoiceWithoutDocumentModal
                                : invoiceFormStore.saveForm(true)
                        }
                        className={`mr-2 ${styles.SaveButton}`}
                        label={saveFormButtonLabel}
                        disabled={invoiceStore.loadingType !== LOADING_TYPE.NONE}
                    />
                    <UI.Button
                        label={t("labels.discard")}
                        disabled={
                            !invoiceStore.currentInvoice?.hasChanges ||
                            invoiceStore.loadingType === LOADING_TYPE.SAVING_INVOICE
                        }
                        onClick={onResetForm}
                        className={styles.DiscardButton}
                        variant="outline-primary"
                    />
                </div>
            )}
        </>
    );
    const mobileViewElements = (
        <div>
            <InvoiceFilePreview
                file={invoiceStore.currentInvoice?.invoiceFile}
                reviewWindowWidth={uiStore.invoiceFilePreviewWidth}
            />
            <InvoiceForm displaySaveAndDiscardButtonRow={true} />
        </div>
    );

    return (
        <>
            {/* Top Navigation / Action Bar */}
            {!invoiceStore.currentInvoice?.isNewInvoice && (
                <div
                    className={`${styles.InvoiceDetailsTopNavigation} ${
                        invoiceStore.isInvoiceBannerDisplayed ? styles.TopSpaceForInvoiceBanner : ""
                    }`}
                    style={{ width: uiStore.contentWidth }}
                >
                    {uiStore.currentScreenSize > SCREEN_SIZE.MOBILE_L ? (
                        <div className={styles.WorkflowAndActionsContainer}>
                            <InvoiceWorkflowTab />
                            <InvoiceBatchProcessTab />
                            <InvoiceActionTab />
                        </div>
                    ) : (
                        <div className={styles.WorkflowAndActionsContainer}>
                            <InvoiceActionTab />
                        </div>
                    )}

                    <div className={styles.tabNavigation}>
                        <div
                            className={`${styles.tabItem} ${
                                activeTab === InvoiceTab.Invoice ? styles.activeTabItem : ""
                            }`}
                            onClick={handleInvoiceTabClick}
                        >
                            <UI.Icon
                                icon={UI.SVGIcon.Documents}
                                color={ColorStyle(activeTab === InvoiceTab.Invoice ? "primary" : "secondary")}
                            />
                            <span>{t("screens.kredi_flow.invoice")}</span>
                        </div>
                        <div
                            className={`${styles.tabItem} ${
                                activeTab === InvoiceTab.History ? styles.activeTabItem : ""
                            }`}
                            onClick={handleHistoryTabClick}
                        >
                            <UI.Icon
                                icon={UI.SVGIcon.Time}
                                color={ColorStyle(activeTab === InvoiceTab.History ? "primary" : "secondary")}
                            />
                            <span>{t("screens.kredi_flow.history.section_title")}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Content-Container */}
            <UI.Container
                className={`${styles.InvoiceDetailsContainer}  ${
                    invoiceStore.currentInvoice?.isNewInvoice ? styles.NewInvoice : ""
                }`}
                fluid
            >
                <div className="px-2 px-md-0">
                    {invoiceStore.currentInvoice?.isNewInvoice && (
                        <UI.Title>{t("screens.kredi_flow.new_invoice")}</UI.Title>
                    )}
                </div>
                <>
                    {activeTab === InvoiceTab.Invoice && (
                        <UI.Row>
                            {invoiceStore.currentInvoice?.hasDuplicates &&
                            invoiceStore.loadingType !== LOADING_TYPE.INITIALIZING ? (
                                <UI.Col xs={12} className={styles.InvoiceDuplicatesWarningBox}>
                                    <div className={`d-flex ${styles.InvoiceDuplicatesWarningBoxContent}`}>
                                        <div className="mr-2">
                                            <UI.Icon icon={UI.SVGIcon.AlertTriangle} />
                                        </div>

                                        <div>
                                            {t("screens.kredi_flow.duplicates.modal.warning")}{" "}
                                            {t("screens.kredi_flow.duplicates.click_button")}
                                        </div>

                                        {/* <div> */}
                                        <UI.Button
                                            className={styles.OpenDuplicatesButton}
                                            label={t("screens.kredi_flow.duplicates.show_duplicates")}
                                            onClick={toggleDisplayDuplicateModal}
                                        />
                                    </div>
                                </UI.Col>
                            ) : undefined}

                            {/* Pinned Comments */}
                            <InvoicePinnedComments />

                            <UI.Col xs={12} className={styles.InvoiceDetailsContentContainer}>
                                {invoiceStore.loadingType === LOADING_TYPE.INITIALIZING ? (
                                    <div className="d-flex align-items-center justify-content-center">
                                        <UI.RotatingSpinner noLogo size={40} className={styles.LoadingTicketSpinner} />
                                        <div className="ml-4">{t("screens.kredi_flow.loading_invoice")}</div>
                                    </div>
                                ) : uiStore.currentScreenSize > SCREEN_SIZE.MOBILE_L ? (
                                    desktopViewElements
                                ) : (
                                    mobileViewElements
                                )}
                            </UI.Col>

                            {invoiceStore.currentInvoice?.displayInvoiceAccountingsSection && (
                                <UI.Col xs={12} className={styles.InvoiceAccountingsContainer}>
                                    <InvoiceAccountings />
                                </UI.Col>
                            )}
                            {uiStore.currentScreenSize <= SCREEN_SIZE.MOBILE_L ? (
                                <div className={styles.InvoiceBatchProcessContainerMobile}>
                                    <InvoiceBatchProcessTab />
                                </div>
                            ) : undefined}
                        </UI.Row>
                    )}

                    {activeTab === InvoiceTab.History && (
                        <UI.Row>
                            <UI.Col className={styles.InvoiceHistoryContainer}>
                                <InvoiceHistory />
                            </UI.Col>
                        </UI.Row>
                    )}
                </>
                <InvoiceDuplicatesModal />
                <AiExtractionInProgressModal />
            </UI.Container>
        </>
    );
};

export const InvoiceDetails = Sentry.withProfiler(observer(InvoiceDetailsBase));
